// DRV-5b(ii) (#2883) — storage-status reads for the filing badge (AC7).
//
// Modelled on AuthorizeGDrivePage's launch poll (a setTimeout chain with an
// `active` flag cleared on unmount); deliberately local to the badge, not a
// shared generic hook.
//
// - One read on mount. Then poll only while the row is PENDING or RUNNING
//   and Drive is connected: 5 s, ×2 per poll, capped at 60 s, back to 5 s
//   whenever the status changes. The sweeper ticks every 15 s, so faster is
//   waste.
// - Stop on DONE / FAILED / SKIPPED / BLOCKED, on `connected: false`, on a
//   403 or 404 (the badge then renders nothing), and while the tab is hidden.
//   Coming back to the tab reads once and carries on by the same rules.
// - PENDING with `notBefore` more than 60 s away (a delegate window): no
//   polling, one read at `notBefore`.
// - A failed first read (5xx / network) is retried once, after 5 s (R1-10).
// - restart(): a 202 (or a 409) from File now / Retry reads at once and
//   carries on from 5 s.
// - A new artefactId (R1-3, R2-3): the old id's read and 404 are dropped (not
//   kept for a return), its timer is cleared and a late read for it is ignored
//   (the effect's cleanup). Switching back starts from a fresh read.
// - Hidden while a read is in flight (R2-2): paused too, so returning reads
//   once and replaces whatever that read scheduled while hidden.
// - markGone(id) (R2-1): a press refused with 403/404 hides the badge and
//   stops reads, exactly like a poll 403/404.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import { isStorageStatus, type StorageStatus } from './filingState';

export const POLL_INITIAL_MS = 5_000;
export const POLL_MAX_MS = 60_000;
/** setTimeout's ceiling; a delegate window is 48 h, well under it. */
const MAX_TIMER_MS = 2_147_483_647;

export function storageStatusPath(artefactId: string): string {
  return `/artefacts/${encodeURIComponent(artefactId)}/storage-status`;
}

/** Poll again only while the filing can still move on its own. */
export function shouldPoll(s: StorageStatus): boolean {
  if (!s.connected) return false;
  return s.filing?.status === 'PENDING' || s.filing?.status === 'RUNNING';
}

/** For a PENDING row waiting out a far-off `notBefore`: ms until it, else null. */
export function farNotBeforeDelay(s: StorageStatus, now: number): number | null {
  if (s.filing?.status !== 'PENDING') return null;
  const at = Date.parse(s.filing.notBefore);
  if (Number.isNaN(at) || at - now <= POLL_MAX_MS) return null;
  return Math.min(at - now, MAX_TIMER_MS);
}

export interface StorageStatusPoll {
  /** The latest readable status; null before the first read or when it was unreadable. */
  status: StorageStatus | null;
  /** When `status` was read (ms); the badge judges `notBefore` against it. */
  readAt: number;
  /** 403 / 404: the caller can't see this artefact's filing. */
  gone: boolean;
  /** Read now and poll again from 5 s (after a File now / Retry press). */
  restart: () => void;
  /** A press for this artefact was refused (403/404): gone, no more reads. */
  markGone: (artefactId: string) => void;
}

export function useStorageStatusPoll(artefactId: string): StorageStatusPoll {
  // Keyed by artefact id (R1-3): a read or a 404 for one artefact is never
  // returned while the hook is pointed at another.
  const [read, setRead] = useState<{ id: string; status: StorageStatus | null; readAt: number }>({ id: artefactId, status: null, readAt: 0 });
  const [goneFor, setGoneFor] = useState<string | null>(null);
  const [run, setRun] = useState(0);
  // R2-3: on an artefact change, drop the old id's state rather than keep it
  // keyed for a later return (state adjusted during render, not in an effect).
  const [shownId, setShownId] = useState(artefactId);
  if (shownId !== artefactId) {
    setShownId(artefactId);
    setRead({ id: artefactId, status: null, readAt: 0 });
    setGoneFor(null);
  }
  const goneNow = goneFor === artefactId;

  useEffect(() => {
    // Gone (a 403/404 from a read or a press): nothing more to read.
    if (goneNow) return;
    let active = true;
    const id = artefactId;
    const setStatus = (s: StorageStatus | null) => setRead({ id, status: s, readAt: Date.now() });
    const setGone = (g: boolean) => setGoneFor(g ? id : null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = POLL_INITIAL_MS;
    let lastStatus: string | null | undefined;
    // Whether the last good read wanted another one (a failed read keeps it).
    let polling = false;
    // A read came due, or was in flight, while the tab was hidden: read on return.
    let paused = false;
    let inFlight = false;
    // R1-10: a failed FIRST read (5xx / network) is retried once, after the
    // back-off; a second failure stops. A 403/404 is final either way.
    let hadRead = false;
    let retriedFirst = false;

    const schedule = (ms: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(tick, ms);
    };
    const backoff = () => {
      schedule(delay);
      delay = Math.min(delay * 2, POLL_MAX_MS);
    };
    const next = (s: StorageStatus) => {
      const st = s.filing?.status ?? null;
      if (st !== lastStatus) { delay = POLL_INITIAL_MS; lastStatus = st; }
      polling = shouldPoll(s);
      if (!polling) return;
      const far = farNotBeforeDelay(s, Date.now());
      if (far !== null) { schedule(far); return; }
      backoff();
    };
    function tick() {
      timer = undefined;
      if (document.visibilityState === 'hidden') { paused = true; return; }
      inFlight = true;
      apiFetch<unknown>(storageStatusPath(id))
        .then((body) => {
          inFlight = false;
          if (!active) return;
          if (!isStorageStatus(body)) { polling = false; setStatus(null); return; }
          hadRead = true;
          setGone(false);
          setStatus(body);
          next(body);
        })
        .catch((err: unknown) => {
          inFlight = false;
          if (!active) return;
          if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
            polling = false;
            setGone(true);
            setStatus(null);
            return;
          }
          // A transient failure: keep the last status and keep to the backoff.
          if (polling) backoff();
          else if (!hadRead && !retriedFirst) { retriedFirst = true; backoff(); }
        });
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timer) { clearTimeout(timer); timer = undefined; paused = true; }
        // R2-2: a read in flight (no timer yet) counts as active polling.
        else if (inFlight) paused = true;
        return;
      }
      if (!paused) return;
      paused = false;
      delay = POLL_INITIAL_MS;
      if (inFlight) return; // it is still coming; it schedules on landing, visibly
      // Replace anything the hidden-time read scheduled (e.g. a far notBefore).
      if (timer) { clearTimeout(timer); timer = undefined; }
      tick();
    };

    document.addEventListener('visibilitychange', onVisibility);
    tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [artefactId, run, goneNow]);

  const restart = useCallback(() => setRun((n) => n + 1), []);
  const markGone = useCallback((id: string) => setGoneFor(id), []);
  const current = read.id === artefactId;
  return { status: current ? read.status : null, readAt: current ? read.readAt : 0, gone: goneNow, restart, markGone };
}
